/**
 * DepartmentTimeline — Sprint 7 · Project Symphony
 *
 * The Living Timeline. Every autonomous action is shown with:
 *  - What happened (label)
 *  - Why it happened (reason)
 *  - Which governance boundary authorised it (customer-safe language)
 *  - Confidence (label + reasons)
 *
 * Executive Amendment 9: never wonder "why did Orkexra do that?"
 */
import { Card } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import type { AutopilotAction } from "../api";

type ActionGroup = {
  signature: string;
  actions: AutopilotAction[];
};

function groupRepeatedActions(actions: AutopilotAction[]): ActionGroup[] {
  return actions.reduce<ActionGroup[]>((groups, action) => {
    const signature = JSON.stringify({
      action_label: action.action_label,
      reason: action.reason,
      pending: action.is_pending_approval,
      confidence_label: action.confidence?.label ?? null,
      confidence_score: action.confidence?.score ?? null,
      confidence_reasons: action.confidence?.reasons ?? [],
    });
    const previous = groups[groups.length - 1];
    if (previous?.signature === signature) {
      previous.actions.push(action);
    } else {
      groups.push({ signature, actions: [action] });
    }
    return groups;
  }, []);
}

function ActionDetails({ action }: { action: AutopilotAction }): JSX.Element {
  return (
    <>
      <p className="break-words text-sm leading-relaxed text-muted-foreground">{action.reason}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Governance: Evaluated against your approved trust level and workspace guardrails.
      </p>
      {action.confidence && action.confidence.reasons.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          Signals: {action.confidence.reasons.join(" · ")}
        </p>
      )}
    </>
  );
}

export function DepartmentTimeline({
  actions,
}: {
  actions: AutopilotAction[];
}): JSX.Element {
  const groups = groupRepeatedActions(actions);
  return (
    <Card className="p-4 sm:p-6" data-testid="department-timeline">
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
        Department Timeline
      </p>

      {actions.length === 0 ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="department-timeline-empty"
        >
          No autonomous actions yet. Enable Autopilot and set a trust level
          — every action will appear here with its full reasoning.
        </p>
      ) : (
        <ol className="space-y-4">
          {groups.map((group, i) => {
            const a = group.actions[0];
            const repeated = group.actions.length > 1;
            return (
              <li
                key={`${group.signature}-${a.occurred_at}-${i}`}
                data-testid="timeline-action-item"
                className="min-w-0 border-l-2 border-border pl-3 sm:pl-4"
              >
                <div className="mb-2 flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-2">
                  <span className="text-xs font-mono text-muted-foreground tabular-nums">
                    {new Date(a.occurred_at).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                  <span className="break-words text-sm font-semibold">{a.action_label}</span>
                  {a.is_pending_approval && <Badge variant="outline">Awaiting Approval</Badge>}
                  {a.confidence && (
                    <Badge variant="secondary" data-testid="timeline-action-confidence">
                      Confidence {a.confidence.label} · {a.confidence.score}
                    </Badge>
                  )}
                  {repeated && (
                    <Badge variant="outline" data-testid="timeline-action-repeat-count">
                      Repeated {group.actions.length} times
                    </Badge>
                  )}
                </div>
                <ActionDetails action={a} />
                {repeated && (
                  <details className="mt-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2" data-testid="timeline-repeated-actions">
                    <summary className="cursor-pointer text-xs font-medium text-primary">
                      Show {group.actions.length - 1} earlier identical {group.actions.length === 2 ? "action" : "actions"}
                    </summary>
                    <ol className="mt-3 space-y-3 border-l border-border/60 pl-3">
                      {group.actions.slice(1).map((item, itemIndex) => (
                        <li key={`${item.occurred_at}-${itemIndex}`}>
                          <div className="mb-1 font-mono text-xs tabular-nums text-muted-foreground">
                            {new Date(item.occurred_at).toLocaleString(undefined, {
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </div>
                          <ActionDetails action={item} />
                        </li>
                      ))}
                    </ol>
                  </details>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
